import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { GiftCard, GiftCardStatus } from '../models/gift-card.model';
import { GiftCardIdKey } from '../config/constants';
import { WindowService } from './window.service';
import { ErrorService } from './error.service';
import { GiftCardApiService } from './gift-card-api.service';
import { Router } from '@angular/router';
import { LocationService } from './location.service';

@Injectable({ providedIn: 'root' })
export class GiftCardStore {
  private readonly giftCard$ = new BehaviorSubject<GiftCard | null>(null);

  constructor(
    private windowService: WindowService,
    private errorService: ErrorService,
    private giftCardApiService: GiftCardApiService,
    private router: Router,
    private locationService: LocationService,
  ) {}

  setGiftCard(giftCard: GiftCard) {
    this.giftCard$.next(giftCard);
    this.windowService.setStorage(GiftCardIdKey(giftCard.locationId), giftCard.uuid);
  }

  watchGiftCard(): Observable<GiftCard | null> {
    return this.giftCard$.asObservable();
  }

  clearGiftCard(locationId: number) {
    this.windowService.removeStorage(GiftCardIdKey(locationId));
    this.giftCard$.next(null);
  }

  async initGiftCard(locationId: number, utmSource: string): Promise<GiftCard> {
    const currentGiftCard = await this.getCurrentGiftCard(locationId);

    if (!currentGiftCard) {
      return this.createNewBlankAndNavigate(locationId, utmSource);
    }

    if (currentGiftCard.status !== GiftCardStatus.Cart) {
      this.clearGiftCard(locationId);
      return this.createNewBlankAndNavigate(locationId, utmSource);
    }

    this.setGiftCard(currentGiftCard);
    return currentGiftCard;
  }

  async getCurrentGiftCard(locationId: number): Promise<GiftCard | null> {
    const giftCard = await firstValueFrom(this.watchGiftCard());
    const giftCardId = giftCard?.uuid || this.windowService.getStorage(GiftCardIdKey(locationId));

    if (giftCardId) {
      try {
        return await firstValueFrom(this.giftCardApiService.getOne(giftCardId));
      } catch (error) {
        const errorMsg = this.errorService.extractApiError(error);

        if (error instanceof HttpErrorResponse && error.status === 404) {
          console.warn('Error fetching gift card:', errorMsg, 'Creating a new gift card');
          this.clearGiftCard(locationId);
          return null;
        }

        throw error;
      }
    }

    return null;
  }

  private async createNewBlankAndNavigate(locationId: number, utmSource: string): Promise<GiftCard> {
    const giftCard = await this.createNewBlank(locationId, utmSource);
    this.navigateToFirstStep();
    return giftCard;
  }

  private async createNewBlank(locationId: number, utmSource: string): Promise<GiftCard> {
    const giftCard = await firstValueFrom(this.giftCardApiService.createOne({ locationId, utmSource }));
    this.setGiftCard(giftCard);
    return giftCard;
  }

  navigateToFirstStep() {
    const location = this.locationService.getLocation();

    if (location) {
      void this.router.navigate([location.uuid, 'services', 'gift-cards', 'value']);
    }
  }
}
